/**
 * 一個攤位圖形可以掛不只一家廠商。
 *
 * 場館圖上有些攤位本來就是一整塊（例如 AMB 的 6E80，裡面二十幾家），
 * 沒辦法一家畫一塊。舊的資料結構是一個圖形對一家：
 * `booth.linked` 放一個攤位號碼字串、`booth.exhibitor` 放一份廠商資料的複本。
 * 新的資料結構兩者都允許放陣列。
 *
 * 這支把兩種形狀收斂成同一種，呼叫端一律拿到陣列。
 *
 * 🔴 不要在呼叫端各自寫 Array.isArray 判斷。
 * 「這個圖形對到哪些攤位號碼」這件事散在六個檔案裡，寫法還不只一種
 * （有的只比 linked，有的是 linked 或 exhibitor 的攤位號碼二選一）。
 * 漏掉其中一處的症狀是「這一攤點得到、隔壁那一攤點不到」，
 * 而且只有在真的掛了多家的攤位上才看得出來 —— 用現在的資料測不出來。
 */

const 成陣列 = (值) => {
	if (值 === undefined || 值 === null || 值 === false || 值 === '') return [];
	if (!Array.isArray(值)) return [值];
	return 值.filter((項) => 項 !== undefined && 項 !== null && 項 !== false && 項 !== '');
};

/**
 * 這個圖形對到哪些攤位號碼。linked 排在前面，找不到才退回 exhibitor 裡的號碼。
 *
 * 取聯集而不是二選一，是為了跟舊的寫法等價：原本有幾處寫的是
 * `b.linked === n || b.exhibitor?.['攤位號碼'] === n`。
 * 後台綁定時 linked 與 exhibitor 是一起寫、一起清的，所以兩邊一致，
 * 聯集不會比舊寫法多match到東西。
 */
export const boothNumbersOf = (booth) => {
	const 來自linked = 成陣列(booth?.linked);
	const 來自廠商 = 成陣列(booth?.exhibitor)
		.map((廠商) => 廠商?.['攤位號碼'])
		.filter(Boolean);

	const 全部 = [...來自linked, ...來自廠商];
	return [...new Set(全部)];
};

/** 這個圖形是不是對到某個攤位號碼。 */
export const boothHasNumber = (booth, boothNumber) => {
	if (!boothNumber) return false;
	return boothNumbersOf(booth).includes(boothNumber);
};

/** 掛在這個圖形上的第一個攤位號碼，畫面上只放得下一個的時候用。 */
export const primaryBoothNumberOf = (booth) => boothNumbersOf(booth)[0];

/**
 * 認「是哪一家廠商」用序號，不用攤位號碼。
 *
 * 攤位號碼是「畫面上畫哪一塊」，同一個號碼可以有很多家（F0112 底下十三家）；
 * 序號才是「這是哪一家」——客戶匯入檔一家一個。收藏、選取、網址參數、產品歸屬
 * 全部認這個 key，不然收藏一家等於整攤都收進去。
 * 沒有序號的舊資料退回攤位號碼，行為跟以前一模一樣。
 */
export const exhibitorKeyOf = (exhibitor) => {
	if (!exhibitor) return '';
	const 序 = exhibitor['序號'];
	const 序字串 = 序 === undefined || 序 === null ? '' : String(序).trim();
	if (序字串) return 序字串;
	return String(exhibitor['攤位號碼'] || '').trim();
};

export const isSameExhibitor = (a, b) => Boolean(a) && Boolean(b) && exhibitorKeyOf(a) === exhibitorKeyOf(b);

export const findExhibitorByKey = (exhibitorInfo, key) => {
	if (!Array.isArray(exhibitorInfo)) return undefined;
	const 要找 = key === undefined || key === null ? '' : String(key).trim();
	if (!要找) return undefined;
	return exhibitorInfo.find((廠商) => exhibitorKeyOf(廠商) === 要找);
};

/** 同一個攤位號碼底下的全部廠商。一家的攤位就是一個元素的陣列。 */
export const exhibitorsAtBooth = (exhibitorInfo, boothNumber) => {
	if (!Array.isArray(exhibitorInfo) || !boothNumber) return [];
	return exhibitorInfo.filter((廠商) => 廠商?.['攤位號碼'] === boothNumber);
};

/** 畫面上只放得下一家、或路線只記攤位號碼的時候，拿那個號碼底下的第一家。 */
export const firstExhibitorAtBooth = (exhibitorInfo, boothNumber) => exhibitorsAtBooth(exhibitorInfo, boothNumber)[0];

const 去重 = (廠商們) => {
	const 看過 = new Set();
	return 廠商們.filter((廠商) => {
		const key = exhibitorKeyOf(廠商);
		if (看過.has(key)) return false;
		看過.add(key);
		return true;
	});
};

/**
 * 把圖形對到的攤位號碼換成現在的廠商資料。
 * 不直接用 booth.exhibitor 那份複本 —— 它是後台綁定當下複製進去的，
 * 收藏狀態、翻譯後的欄位都不在裡面。
 *
 * 一個號碼底下有幾家就回幾家（filter 不是 find）。
 * 舊寫法用 find 只回第一家，F0112 那十三家永遠只點得到第一家——這正是客戶報的那個問題。
 */
export const boothExhibitorsOf = (booth, exhibitorInfo) => {
	if (!Array.isArray(exhibitorInfo)) return [];
	return 去重(boothNumbersOf(booth).flatMap((號) => exhibitorsAtBooth(exhibitorInfo, 號)));
};

// 比名稱時把空白全部拿掉、不分大小寫：產品檔跟廠商檔是兩個人打的，「德川 機械」與「德川機械」要算同一家。
const 正規化名稱 = (值) => String(值 === undefined || 值 === null ? '' : 值).replace(/\s+/g, '').toLowerCase();

/**
 * 一筆產品資料是哪一家的。
 *
 * 產品檔只有攤位號碼與廠商名稱（它的「序號」是產品自己的流水號，不是廠商序號——
 * 拿 TIE 的 1970 筆對過，只有 9 筆湊巧對上）。
 * 同一個攤位號碼只有一家時直接對號碼，跟以前一樣，廠商名稱打錯也不影響；
 * 多家共用號碼時才比廠商名稱（其次廠商簡稱），對不上的那筆就不歸任何一家。
 */
export const exhibitorOfProduct = (exhibitorInfo, product) => {
	const 候選 = exhibitorsAtBooth(exhibitorInfo, product?.['攤位號碼']);
	if (候選.length <= 1) return 候選[0];
	const 名 = 正規化名稱(product?.['廠商名稱']);
	if (!名) return undefined;
	return 候選.find((廠商) => 正規化名稱(廠商['廠商名稱']) === 名) || 候選.find((廠商) => 正規化名稱(廠商['廠商簡稱']) === 名);
};

/** 這家廠商的產品。單獨一家的攤位照舊只看攤位號碼；多家共用才再用廠商名稱分。 */
export const productsOfExhibitor = (productInfo, exhibitorInfo, exhibitor) => {
	const 號 = exhibitor?.['攤位號碼'];
	if (!號 || !Array.isArray(productInfo)) return [];
	const 這攤的 = productInfo.filter((產品) => 產品?.['攤位號碼'] === 號);
	if (exhibitorsAtBooth(exhibitorInfo, 號).length <= 1) return 這攤的;
	return 這攤的.filter((產品) => isSameExhibitor(exhibitorOfProduct(exhibitorInfo, 產品), exhibitor));
};

/**
 * 某個攤位號碼落在哪一個圖形上。掃全部樓層，回 { floorIndex, booth }，找不到回 null。
 */
export const boothOfNumber = (floorInfo, boothNumber) => {
	if (!Array.isArray(floorInfo) || !boothNumber) return null;
	for (let floorIndex = 0; floorIndex < floorInfo.length; floorIndex++) {
		const booths = floorInfo[floorIndex]?.booths || [];
		const booth = booths.find((候選) => boothHasNumber(候選, boothNumber));
		if (booth) return { floorIndex, booth };
	}
	return null;
};

/**
 * 跟這家廠商同攤位的其他廠商，不含自己。
 * 「同攤位」= 掛在同一個圖形上的，加上攤位號碼相同的（圖形還沒畫也算得到）。
 * 單獨一家、或什麼都找不到，都回空陣列——呼叫端不用另外判斷。
 *
 * 排除自己要用序號比，不能用攤位號碼比：F0112 底下十三家的號碼全都一樣，
 * 用號碼比會把十三家全部排掉，右欄永遠是空的。
 */
export const boothMatesOf = (floorInfo, exhibitorInfo, exhibitor) => {
	const 本人 = typeof exhibitor === 'string' ? firstExhibitorAtBooth(exhibitorInfo, exhibitor) : exhibitor;
	if (!本人) return [];
	const 號 = 本人['攤位號碼'];
	const 命中 = boothOfNumber(floorInfo, 號);
	const 同圖形 = 命中 ? boothExhibitorsOf(命中.booth, exhibitorInfo) : [];
	return 去重([...同圖形, ...exhibitorsAtBooth(exhibitorInfo, 號)]).filter((廠商) => !isSameExhibitor(廠商, 本人));
};
